import React, { useEffect, useState } from "react";
import { Snackbar, Alert } from "@mui/material";
import "../CreateClinicalNote/CreateClinicalNote.css";

import LocationForm from "../CreateClinicalNote/CreateClinicalNoteComp/LocationForm";
import VitalsForm from "../CreateClinicalNote/CreateClinicalNoteComp/VitalsForm";
import HpiForm from "../CreateClinicalNote/CreateClinicalNoteComp/HpiForm";
import Phq2Form from "../CreateClinicalNote/CreateClinicalNoteComp/Phq2Form";
import AssessmentsPlanFollowUp from "../CreateClinicalNote/CreateClinicalNoteComp/AssessmentsPlanFollowUp";
import ActionsBar from "../CreateClinicalNote/CreateClinicalNoteComp/ActionsBar";
import RosFormTemplates from "../CreateClinicalNote/CreateClinicalNoteComp/RosFormTemplates";
import ExamFormTemplates from "../CreateClinicalNote/CreateClinicalNoteComp/ExamFormTemplates";

import buildEhrNotePayload from "../../../utils/services/APP/ClinicalNotes/buildEhrNotePayload";
import adaptFormStateToBuilder from "../../../utils/services/APP/ClinicalNotes/adaptFormStateToBuilder";
import { addEhrNote } from "../../../utils/services/AdvancedMD/ehrNotes";

const toUsDatetime = (date = new Date()) => {
  const d = new Date(date);
  const pad = (n) => (n < 10 ? `0${n}` : n);
  const h = d.getHours();
  const hour12 = ((h + 11) % 12) + 1;
  const ampm = h >= 12 ? "PM" : "AM";
  return `${pad(d.getMonth() + 1)}/${pad(d.getDate())}/${d.getFullYear()} ${pad(hour12)}:${pad(d.getMinutes())}:00 ${ampm}`;
};

const TEMPLATE_NAME = "Hospital Discharge Follow Up";

export default function CreateHospitalDischargeFollowUpNote({ patientId, onCancel, onSaved }) {
  const [patient, setPatient] = useState({ patientId: "", profileId: "3" });
  const [meta, setMeta] = useState({ templateName: TEMPLATE_NAME });

  const [location, setLocation] = useState({});
  const [reason, setReason] = useState({});
  const [vitals, setVitals] = useState({});
  const [hpi, setHpi] = useState({});
  const [meds, setMeds] = useState({});
  const [pmh, setPmh] = useState({});
  const [ros, setRos] = useState({});
  const [exam, setExam] = useState({});
  const [phq2, setPhq2] = useState({});
  const [assessPlanFu, setAssessPlanFu] = useState({});

  const [saving, setSaving] = useState(false);
  const [snack, setSnack] = useState({ open: false, severity: "success", message: "" });

  useEffect(() => {
    setPatient({ patientId, profileId: "3" });
    setMeta({
      templateName: TEMPLATE_NAME,
      comment: "Hospital discharge note - follow-up",
      noteDatetime: toUsDatetime(),
      msgtime: toUsDatetime(),
      nocookie: "0",
    });

    setLocation({
      location: "Palm Springs/Lake Worth Sante Plus Medical Center",
      location2: "3155 Lake Worth Rd, Palm Springs, FL-16917",
      location1: "(561) 858-8817 - (561) 878-8277",
    });

    setReason({ ReasonforAppointment: "NP ER Discharge FU" });
  }, [patientId]);

  // Auto BMI — VitalsForm también lo calcula; este es el seguro local
  useEffect(() => {
    if (vitals.Vitals_Height && vitals.Vitals_Weight) {
      const h = parseFloat(vitals.Vitals_Height);
      const w = parseFloat(vitals.Vitals_Weight);
      if (!isNaN(h) && !isNaN(w) && h > 0) {
        const bmi = (w / (h * h)) * 703;
        setVitals((v) => ({ ...v, Vitals_BMI: bmi.toFixed(1) }));
      }
    }
  }, [vitals.Vitals_Height, vitals.Vitals_Weight]);

  const validate = () => {
    if (!patient.patientId) return "Missing patient ID.";
    if (!reason.ReasonforAppointment?.trim()) return "Reason for appointment is required.";
    const hasHpi = !!hpi.HistoryofPresentIllness?.trim() || !!hpi.HistoryofPresentIllness1?.trim();
    const hasAssess = !!assessPlanFu.Assessments?.trim();
    if (!hasHpi && !hasAssess) return "Add at least HPI or an Assessment before saving.";
    return null;
  };

  const onSave = async () => {
    const err = validate();
    if (err) {
      setSnack({ open: true, severity: "warning", message: err });
      return;
    }

    setSaving(true);
    const nowStr = toUsDatetime();

    const adapted = adaptFormStateToBuilder({
      patient,
      meta: { ...meta, msgtime: nowStr, noteDatetime: nowStr },
      location,
      reason,
      vitals,
      hpi,
      meds,
      pmh,
      ros,
      exam,
      phq2,
      assessPlanFu,
    });

    const payload = buildEhrNotePayload(adapted);

    try {
      const result = await addEhrNote(payload);
      const idTxt = result?.id ? ` (ID: ${result.id})` : "";
      setSnack({ open: true, severity: "success", message: `Clinical note created${idTxt}.` });
      onSaved?.(result);
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || "Unknown error";
      setSnack({ open: true, severity: "error", message: `Error creating note: ${msg}` });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="cn-container">
      <h2>Hospital Discharge Follow Up</h2>

      <LocationForm
        location={location}
        reason={reason}
        onChangeLocation={setLocation}
        onChangeReason={setReason}
        isEditing={!saving}
      />

      <VitalsForm vitals={vitals} onChange={setVitals} templateId={null} isEditing={!saving} />
      <HpiForm
        hpi={hpi}
        meds={meds}
        pmh={pmh}
        onChangeHpi={setHpi}
        onChangeMeds={setMeds}
        onChangePmh={setPmh}
        isEditing={!saving}
      />
      <RosFormTemplates ros={ros} onChange={setRos} templateId={null} isEditing={!saving} />
      <ExamFormTemplates exam={exam} onChange={setExam} isEditing={!saving} />
      <Phq2Form phq2={phq2} onChange={setPhq2} isEditing={!saving} />
      <AssessmentsPlanFollowUp data={assessPlanFu} onChange={setAssessPlanFu} isEditing={!saving} />

      <ActionsBar
        onSave={onSave}
        onCancel={onCancel}
        saveLabel="Create Note"
        saving={saving}
      />

      <Snackbar
        open={snack.open}
        autoHideDuration={5000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          severity={snack.severity}
          variant="filled"
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </div>
  );
}
